import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useSubscription } from '@apollo/client';
import { LIST_CALL_TRANSCRIPTS, ON_TRANSCRIPT_UPDATE } from '../../graphql/operations';

const INITIAL_FETCH_LIMIT = 200;

// Target speech reveal rate for the paced-reveal layer (chars/sec). OpenAI's
// Realtime API bursts every `response.output_audio_transcript.delta` for an
// assistant turn at text-gen speed (~26 deltas in ~800ms for a ~12s spoken
// utterance), but the synthesized audio plays back at natural speech rate.
// We pace the bubble to read along with the audio rather than racing it.
// 15 chars/sec is a practical mid-range for English narration (~150 wpm at
// ~5 chars/word with whitespace).
export const CHARS_PER_SECOND = 15;
// When a final segment arrives and we're within this many unrevealed chars,
// snap to the final text instead of slowly catching up. Keeps the bubble
// from lagging visibly behind the spoken audio's natural tail.
export const SNAP_REMAINDER_CHARS = 20;

// Backend persists each transcript `.delta` as a separate TRANSCRIPT# row with
// segmentId `<itemId>#delta#<seq>` so deltas remain individually queryable for
// audit. The `.done` event lands with the canonical itemId (no `#delta#` suffix).
// Without grouping, every delta renders as its own bubble in the live drawer,
// followed by one final coherent bubble — the UX problem the user reported.
// This helper strips the `#delta#<seq>` suffix to derive the canonical group id.
function canonicalSegmentId(segmentId) {
  const value = String(segmentId || '');
  const idx = value.indexOf('#delta#');
  return idx === -1 ? value : value.slice(0, idx);
}

function compareSegments(a, b) {
  const aSeq = Number(a.sequenceNumber || 0);
  const bSeq = Number(b.sequenceNumber || 0);
  if (aSeq !== bSeq) return aSeq - bSeq;
  return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
}

// Collapse deltas into one logical bubble per (callId, canonicalSegmentId).
// If a `.done` exists in the group (isFinal === true), its content is the
// authoritative final transcript. Otherwise concatenate the deltas in order to
// produce a single in-progress bubble that grows live and keeps the caret.
function collapseDeltas(segments) {
  const groups = new Map();
  for (const segment of segments) {
    if (!segment || !segment.callId || !segment.segmentId) continue;
    const key = `${segment.callId}:${canonicalSegmentId(segment.segmentId)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(segment);
  }
  const collapsed = [];
  for (const [, group] of groups) {
    group.sort(compareSegments);
    const done = group.find((s) => s.isFinal === true);
    if (done) {
      // Authoritative final — keep done's segmentId so React's key is stable
      // once the turn finalizes (done's id is the canonical id).
      collapsed.push({ ...done });
    } else {
      // Still streaming — emit one in-progress segment whose content is the
      // concatenation of all deltas. Anchor identity to the canonical id so
      // the same DOM node carries from delta→delta→done (no flicker).
      const first = group[0];
      const concatenated = group.map((s) => String(s.content || '')).join('');
      collapsed.push({
        ...first,
        segmentId: canonicalSegmentId(first.segmentId),
        content: concatenated,
        isFinal: false,
      });
    }
  }
  return collapsed.sort(compareSegments);
}

// Identify assistant-side segments. Only assistant bubbles need pacing —
// caller bubbles already arrive at speaking pace via realtime ASR, and
// system bubbles are instantaneous status lines.
function isAssistantSegment(segment) {
  const value = String(segment?.speaker || '').toLowerCase();
  return value === 'ai' || value === 'assistant' || value === 'agent';
}

// ---------------------------------------------------------------------------
// Paced-reveal layer
// ---------------------------------------------------------------------------
//
// Goal: when an assistant turn streams in, grow `displayContent` toward
// `content` at roughly `CHARS_PER_SECOND` so the supervisor reads along
// with the caller's audio rather than seeing the full text pop in within
// ~1s while audio is still playing.
//
// We expose a pure helper for testability and a `usePacedSegments` hook
// that drives state via `requestAnimationFrame`. Tests can call
// `advancePacedReveal` directly without spinning a real RAF loop.

/**
 * Pure pacing step. Given the previous revealed counter, the full content,
 * the elapsed milliseconds since the last tick, the segment's `isFinal`
 * flag, and the snap threshold, return the next revealed-char count.
 *
 * - In-progress (`isFinal=false`): advance by `elapsedMs * rate / 1000`,
 *   clamped to content length.
 * - Final (`isFinal=true`) with remaining ≤ snapRemainder: jump to full.
 * - Final with larger remainder: keep advancing at the same rate; the
 *   audio is still playing and the bubble should not race ahead.
 */
export function advancePacedReveal({
  previousRevealed,
  contentLength,
  elapsedMs,
  isFinal,
  charsPerSecond = CHARS_PER_SECOND,
  snapRemainder = SNAP_REMAINDER_CHARS,
}) {
  const safePrev = Math.max(0, Math.min(Number(previousRevealed) || 0, contentLength));
  if (contentLength === 0) return 0;
  if (isFinal && contentLength - safePrev <= snapRemainder) {
    return contentLength;
  }
  const delta = Math.max(0, Number(elapsedMs) || 0) * (charsPerSecond / 1000);
  const next = safePrev + delta;
  return Math.min(contentLength, next);
}

/**
 * usePacedSegments
 *
 * Wraps each segment with a `displayContent` field. Caller, system, and
 * already-final assistant segments are passed through with
 * `displayContent === content`. Assistant in-progress segments get a
 * RAF-driven reveal capped at `CHARS_PER_SECOND`.
 *
 * Behaviour notes:
 *   - First time a segment is observed, the reveal counter is seeded at
 *     its current content length. Switching calls (or opening the drawer
 *     mid-turn) does not animate from zero — we don't have audio-position
 *     events to know where the caller is, so we treat already-loaded text
 *     as already-revealed and pace only the new growth from that point on.
 *   - When new content arrives (deltas), the counter keeps its prior value
 *     and the RAF loop catches up at the configured rate.
 *   - When `isFinal` flips to true with a small remainder, we snap to full.
 *   - The RAF loop only runs while at least one assistant segment still
 *     has unrevealed characters; otherwise it idles (no wasted frames).
 *   - On `callId` change, all pacing state resets.
 *   - StrictMode-safe: the RAF handle is tracked in a ref and a guard
 *     ignores stale frames issued by an unmounted effect.
 */
function usePacedSegments(segments, callId) {
  // Map<segmentId, { revealed: number, lastTickMs: number }>
  const stateRef = useRef(new Map());
  const rafRef = useRef(null);
  const lastCallIdRef = useRef(callId);
  // Tick counter forces re-render when the RAF loop advances. Using a
  // counter rather than copying the Map into state keeps allocations down.
  const [tick, setTick] = useState(0);

  // Reset all pacing state when the active call changes. We do this in
  // render (synchronously) so the next derived output uses the cleared
  // map immediately — an effect would leave a stale frame visible.
  if (lastCallIdRef.current !== callId) {
    lastCallIdRef.current = callId;
    stateRef.current = new Map();
  }

  // Seed/refresh entries for assistant segments that need pacing.
  // For segments first seen here, seed at current content length so the
  // bubble shows what was already loaded without re-animating from zero.
  // For segments we already know about, leave `revealed` alone — the RAF
  // loop owns advancement.
  for (const segment of segments) {
    if (!isAssistantSegment(segment)) continue;
    const id = segment.segmentId;
    if (!id) continue;
    const contentLength = String(segment.content || '').length;
    const entry = stateRef.current.get(id);
    if (!entry) {
      // First sight. If this is already final, no need to animate.
      stateRef.current.set(id, {
        revealed: contentLength,
        lastTickMs: 0,
      });
    } else if (entry.revealed > contentLength) {
      // Content shrank (rare — would only happen on a server-side rewrite).
      // Clamp so we don't read past the end.
      entry.revealed = contentLength;
    }
  }

  // Drive the RAF loop. It runs while any tracked assistant segment is
  // behind its full content. The loop is started/stopped from the same
  // effect, with a guard to make StrictMode's double-mount harmless.
  useEffect(() => {
    let cancelled = false;

    function anyBehind() {
      for (const segment of segments) {
        if (!isAssistantSegment(segment)) continue;
        const id = segment.segmentId;
        const entry = stateRef.current.get(id);
        if (!entry) continue;
        const contentLength = String(segment.content || '').length;
        if (entry.revealed < contentLength) return true;
      }
      return false;
    }

    function step(now) {
      if (cancelled) return;
      let advanced = false;
      for (const segment of segments) {
        if (!isAssistantSegment(segment)) continue;
        const id = segment.segmentId;
        const entry = stateRef.current.get(id);
        if (!entry) continue;
        const contentLength = String(segment.content || '').length;
        if (entry.revealed >= contentLength) continue;
        const elapsedMs = entry.lastTickMs ? now - entry.lastTickMs : 16;
        const next = advancePacedReveal({
          previousRevealed: entry.revealed,
          contentLength,
          elapsedMs,
          isFinal: segment.isFinal === true,
        });
        if (next !== entry.revealed) {
          entry.revealed = next;
          advanced = true;
        }
        entry.lastTickMs = now;
      }
      if (advanced) {
        setTick((n) => (n + 1) % 1_000_000);
      }
      if (!cancelled && anyBehind()) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        rafRef.current = null;
      }
    }

    if (anyBehind() && typeof requestAnimationFrame === 'function') {
      rafRef.current = requestAnimationFrame(step);
    }

    return () => {
      cancelled = true;
      if (rafRef.current && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = null;
    };
    // We intentionally depend on `segments` identity. `collapseDeltas`
    // returns a fresh array each delta arrival, which is exactly when we
    // need to re-evaluate whether the loop should resume. `tick` is
    // intentionally not a dep — its sole job is to schedule a re-render.
  }, [segments]);

  // Derive paced output. Pure mapping over `segments` using the current
  // reveal counters; React re-runs this on every `tick`.
  return useMemo(() => {
    return segments.map((segment) => {
      const content = String(segment.content || '');
      if (!isAssistantSegment(segment)) {
        return { ...segment, displayContent: content };
      }
      const entry = stateRef.current.get(segment.segmentId);
      const revealed = entry ? Math.max(0, Math.min(Math.floor(entry.revealed), content.length)) : content.length;
      return { ...segment, displayContent: content.slice(0, revealed) };
    });
    // `tick` is read implicitly so the memo refreshes when the RAF loop
    // advances; `segments` covers content/finality changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, tick]);
}

export default function useTranscriptStream(callId) {
  const [extraSegments, setExtraSegments] = useState([]);
  const lastCallIdRef = useRef(callId);

  // Reset accumulated subscription segments when the active call changes.
  useEffect(() => {
    if (lastCallIdRef.current !== callId) {
      lastCallIdRef.current = callId;
      setExtraSegments([]);
    }
  }, [callId]);

  const { data: initialData, loading: initialLoading, error: initialError, refetch } = useQuery(
    LIST_CALL_TRANSCRIPTS,
    {
      variables: { callId, limit: INITIAL_FETCH_LIMIT },
      skip: !callId,
      fetchPolicy: 'cache-and-network',
    }
  );

  useSubscription(ON_TRANSCRIPT_UPDATE, {
    variables: { callId },
    skip: !callId,
    onData: ({ data }) => {
      const segment = data?.data?.onTranscriptUpdate;
      if (!segment) return;
      setExtraSegments((current) => [...current, segment]);
    },
  });

  const collapsedSegments = useMemo(() => {
    const base = initialData?.listCallTranscripts || [];
    return collapseDeltas([...base, ...extraSegments]);
  }, [initialData, extraSegments]);

  // Paced layer: adds `displayContent` to each segment. Assistant
  // in-progress bubbles reveal at ~CHARS_PER_SECOND; everything else
  // passes through with displayContent === content.
  const segments = usePacedSegments(collapsedSegments, callId);

  return {
    segments,
    loading: initialLoading && segments.length === 0,
    error: initialError,
    refetch,
  };
}
